import { components } from "@certn/api/generated-schemas/client-portal";
import { Card, Link, mq, Text } from "@certn/cascadia-react";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { Trans, useTranslation } from "react-i18next";

import { PASS_THROUGH_ACCESS_FEES_URL } from "../../constants";
import { SummaryItemWithChecks } from "./components/SummaryItemWithChecks";
import { useCanViewClientPortalPricing } from "../permissions/Permissions";
import { SelectedChecksType } from "../../pages/order/OrderContext";
import { getOrderCost } from "../../pages/order/order-utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { formatNullableCurrency } from "../../utils/formatNullableCurrency";
import { getSelectedUmbrellaOptions } from "../../utils/getSelectedUmbrellaOptions";
import { AddOnOrderSummaryContainer } from "../ordering-check-card/components/AddOnOrderSummaryContainer";

interface Props {
  checks: SelectedChecksType[];
  bundles?: components["schemas"]["AvailableBundlesList"][];
  title?: string;
}

export function OrderSummary({ checks, bundles = [], title }: Props) {
  const {
    enableAddressBasedInternationalCriminalRecordCheckOrdering,
    enableAddressBasedBankruptcyCheckOrdering,
    enableAddressBasedDirectorshipCheckOrdering,
    enableAddressBasedCreditCheckOrdering,
    enableCivilLitigationRecordCheck,
  } = useFlags();

  const isAddressBasedEnabledForCheck = (checkKey: string) =>
    (checkKey === "CRIMINAL_RECORD_REPORT_1" &&
      enableAddressBasedInternationalCriminalRecordCheckOrdering) ||
    (checkKey === "BANKRUPTCY_RECORD_CHECK_1" &&
      enableAddressBasedBankruptcyCheckOrdering) ||
    (checkKey === "DIRECTORSHIP_RECORD_CHECK_1" &&
      enableAddressBasedDirectorshipCheckOrdering) ||
    (checkKey === "CREDIT_REPORT_1" && enableAddressBasedCreditCheckOrdering) ||
    (checkKey === "CIVIL_LITIGATION_RECORD_CHECK_1" &&
      enableCivilLitigationRecordCheck);

  const { t } = useTranslation();
  const canViewClientPortalPricing = useCanViewClientPortalPricing();
  const totalCost = getOrderCost(checks, bundles);
  const isEmpty = !checks.length && !bundles.length;
  const summaryCurrency = checks[0]?.currency ?? bundles[0]?.currency;
  const formattedSubtotal = formatNullableCurrency(Number(totalCost), {
    currency: summaryCurrency,
    locale: "en-US",
    fallback: null,
  });

  const hasAddressBasedOrdering = (check: SelectedChecksType) =>
    getSelectedUmbrellaOptions(check).some(
      (option) =>
        option.ordering_type === "MULTI_REGION" ||
        option.ordering_type === "SINGLE_REGION" ||
        option.ordering_type === "CURRENT_REGION"
    ) && isAddressBasedEnabledForCheck(check.key);

  const orderContainsCheckWithMultiRegion: boolean = checks.some(
    hasAddressBasedOrdering
  );

  const renderFromPrefix = (check: SelectedChecksType) => {
    if (check.price_is_catalog_minimum) {
      return t("From ");
    }
    if (
      hasAddressBasedOrdering(check) ||
      check.check_arguments?.include_previous_names === true
    ) {
      return t("From ");
    }
    return "";
  };

  return (
    <Card
      className={styles.container}
      border="none"
      testID="order-summary-card"
    >
      <div className={css(styles.flexColumn, styles.content)}>
        {title && (
          <Text
            as="h2"
            style="global/heading-large"
            className={css({ paddingBottom: "var(--space-2)" })}
          >
            {title}
          </Text>
        )}
        {isEmpty && (
          <Text color="text-weak">
            {t("When checks are added, they will appear here.")}
          </Text>
        )}
        {bundles.map((bundle) => (
          <SummaryItemWithChecks
            key={bundle.id}
            checks={bundle.products.map(({ id, name }) => ({
              id,
              name,
            }))}
            currency={bundle.currency}
            name={bundle.title}
            price={bundle.total}
            title={t("BUNDLED DEAL")}
          />
        ))}
        {checks.map((check) => {
          // Some other components use selectedOptions, while others expect selected_options,
          // so I added it here to fix the issue with selected templates not showing, without breaking existing functionality.
          const options = getSelectedUmbrellaOptions(check);
          const formattedCheckCost = formatNullableCurrency(check.cost, {
            currency: check.currency,
            locale: "en-US",
            fallback: null,
          });

          return (
            <div className={styles.checks} key={check.key}>
              <div className={css(styles.spaceBetween)}>
                <Text testID={`summary-${slugify(check.name)}-cart`}>
                  {check.name}
                </Text>
                <Text testID={`summary-${slugify(check.name)}-price`}>
                  {canViewClientPortalPricing && formattedCheckCost
                    ? `${renderFromPrefix(check)}${formattedCheckCost}`
                    : ""}
                </Text>
              </div>
              {options.map((option, index) => {
                if (option.label) {
                  return (
                    <Text
                      as="div"
                      style="global/metric-detail"
                      color="text-weak"
                      key={`${option.value}-${index}`}
                    >
                      {option.label}
                    </Text>
                  );
                }
              })}
            </div>
          );
        })}
        <AddOnOrderSummaryContainer
          regularChecks={checks}
          packageChecks={[]}
          canViewClientPortalPricing={canViewClientPortalPricing}
        />
      </div>
      {!isEmpty && canViewClientPortalPricing && (
        <div className={css(styles.flexColumn, styles.footer)}>
          <div className={styles.spaceBetween}>
            <Text style="global/heading-medium" testID="subtotal-label">
              {t("Subtotal")}
            </Text>
            <Text style="global/heading-small" testID="subtotal-price">
              {formattedSubtotal ? `${t("From ")}${formattedSubtotal}` : ""}
            </Text>
          </div>
          <div>
            {orderContainsCheckWithMultiRegion && (
              <Trans>
                <Text color="text-weak">
                  Final cost depends on applicant-provided information.{" "}
                </Text>
              </Trans>
            )}
            <Trans>
              <Text color="text-weak">Pass-through access fees may apply.</Text>
              <Link
                className={styles.feesLink}
                linkStyle="monochrome"
                textStyle="global/body-secondary-link"
                textColor="text-weak"
                href={PASS_THROUGH_ACCESS_FEES_URL}
                testID="order-summary-access-fees-link"
                target="_blank"
              >
                Learn more
              </Link>
            </Trans>
          </div>
        </div>
      )}
    </Card>
  );
}

const styles = {
  container: css(
    {
      display: "flex",
      flexDirection: "column",
      height: "100%",
    },
    mq({
      width: ["auto", "auto", 360],
    })
  ),
  content: css(
    {
      gap: "var(--space-2)",
      flex: 1,
      overflow: "auto",
    },
    mq({
      padding: [
        "0 0 var(--space-2)",
        "0 0 var(--space-2)",
        "var(--space-5) var(--space-6) 0",
      ],
    })
  ),
  checks: css({
    width: "auto",
    paddingTop: "var(--space-2)",
    paddingBottom: "var(--space-2)",
  }),
  footer: css(
    {
      gap: "var(--space-2)",
      borderTop: `1px solid var(--border-base)`,
    },
    mq({
      padding: [
        "var(--space-2) 0 var(--space-10)",
        "var(--space-2) 0 var(--space-10)",
        "var(--space-2) var(--space-4) var(--space-6)",
      ],
    })
  ),
  spaceBetween: css({
    display: "flex",
    justifyContent: "space-between",
  }),
  flexColumn: css({
    display: "flex",
    flexDirection: "column",
  }),
  feesLink: css({
    marginLeft: "var(--space-1)",
  }),
  strikethrough: css({ textDecoration: "line-through" }),
};
